/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// DepletionsPage.jsx — ported from the source app. Adapted for Brand Command:
// Supabase-Auth + RLS service (no MSAL), shared referenceDataService for the
// SKU/geography master data (no admin-module import), uuid ids (no Number()
// coercion), and the OneDrive "Refresh Data" Graph sync replaced with a plain
// "Import" button that opens the spreadsheet upload modal. Excel (SheetJS) is
// loaded on demand so it stays out of the main bundle.
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  loadDepletions,
  deleteDepletion,
  loadDistributorLastUpdatedDepletions,
} from "../utils/depletionService.js";
import {
  listSalesStates,
  listFlavors,
  listSizes,
  listPackSizes,
  listProducts,
} from "../../../shared/utils/referenceDataService.js";
import DepletionsChart from "./DepletionsChart.jsx";
import DepletionsGrid from "./DepletionsGrid.jsx";
import MobileFilterDrawer from "./MobileFilterDrawer.jsx";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import ExportOptionsModal from "./ExportOptionsModal.jsx";
import ImportModal from "./ImportModal.jsx";
import { exportBranded, LOGO_NOTICE } from "../utils/brandedExport.js";
import { depletionSeries, buildTimelineChartSvg, svgToPng } from "../utils/chartImage.js";
import "./di-tokens.css";
import "./di-dashboard.css";
import "./DepletionsPage.css";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];


const GROUP_BY_OPTIONS = [
  { value: "",             label: "All Dimensions" },
  { value: "flavor_size",  label: "Flavor + Size"  },
  { value: "region",       label: "Region"         },
  { value: "state",        label: "State"          },
  { value: "distributor",  label: "Distributor"    },
  { value: "flavor",       label: "Flavor"         },
  { value: "size",         label: "Bottle Size"    },
  { value: "pkg",          label: "Package Size"   },
];

// Rendered always, but disabled without retail-account detail — grouping by Store
// on a state-level dataset would produce one "—" row, yet hiding the options
// entirely means nobody learns the grouping exists.
const OUTLET_GROUP_BY_OPTIONS = [
  { value: "chain",         label: "Chain"          },
  { value: "account",       label: "Store"          },
  { value: "chain_account", label: "Chain + Store"  },
];

// Chart group-by options (the export chart needs a real dimension — drop the
// "All Dimensions" pivot option, which has no meaningful series).
const CHART_GROUP_BY_OPTIONS = GROUP_BY_OPTIONS.filter(o => o.value !== "");

// Shown on the disabled Chain / Store filters. Names the exact sheet columns, so
// the fix is actionable without leaving the screen.
const NO_OUTLET_DETAIL_HINT =
  "Your depletion data has no store detail yet. Add Chain / Store / Address / City " +
  "columns to your depletions sheet (all optional) and these filters switch on.";

const CURRENT_YEAR = new Date().getFullYear();

// Replicates DepletionsGrid pivot logic for export — same grouped+pivoted structure shown on screen
function buildExportPivot(rows, filterYear, groupBy) {
  const multiYear   = !filterYear;
  const visibleRows = filterYear ? rows.filter(r => r.year === Number(filterYear)) : rows;

  const configs = {
    "":            { dimLabels: ["State","Distributor","Flavor","Bottle Size","Package"], getKey: r => `${r.states?.state_name??""}\x00${r.states?.distributor??""}\x00${r.flavors?.name??""}\x00${r.sizes?.name??""}\x00${r.pack_sizes?.name??""}`, getVals: r => [r.states?.state_name??"—",r.states?.distributor??"—",r.flavors?.name??"—",r.sizes?.name??"—",r.pack_sizes?.name??"—"] },
    "region":      { dimLabels: ["Region"],              getKey: r => r.states?.region??"-",       getVals: r => [r.states?.region??"—"] },
    "state":       { dimLabels: ["State"],               getKey: r => r.states?.state_name??"-",   getVals: r => [r.states?.state_name??"—"] },
    "distributor": { dimLabels: ["Distributor"],         getKey: r => r.states?.distributor??"-",  getVals: r => [r.states?.distributor??"—"] },
    "flavor":      { dimLabels: ["Flavor"],              getKey: r => r.flavors?.name??"-",        getVals: r => [r.flavors?.name??"—"] },
    "size":        { dimLabels: ["Bottle Size"],         getKey: r => r.sizes?.name??"-",          getVals: r => [r.sizes?.name??"—"] },
    "pkg":         { dimLabels: ["Package"],             getKey: r => r.pack_sizes?.name??"-",     getVals: r => [r.pack_sizes?.name??"—"] },
    "flavor_size": { dimLabels: ["Flavor","Bottle Size"],getKey: r => `${r.flavors?.name??""}\x00${r.sizes?.name??""}`, getVals: r => [r.flavors?.name??"—",r.sizes?.name??"—"] },
    "chain":       { dimLabels: ["Chain"],               getKey: r => r.chain_name??"-",           getVals: r => [r.chain_name??"—"] },
    "account":     { dimLabels: ["Store"],               getKey: r => r.account_name??"-",         getVals: r => [r.account_name??"—"] },
    "chain_account": { dimLabels: ["Chain","Store","Address","City"], getKey: r => `${r.chain_name??""}\x00${r.account_name??""}\x00${r.account_address??""}\x00${r.account_city??""}`, getVals: r => [r.chain_name??"—",r.account_name??"—",r.account_address??"—",r.account_city??"—"] },
  };
  // The ungrouped export mirrors what's on screen: with store detail present the
  // grid shows Chain/Store/City, so the export has to as well or they diverge.
  if (!groupBy && rows.some(r => r.chain_name || r.account_name)) {
    configs[""] = {
      dimLabels: ["Chain","Store","Address","City","State","Distributor","Flavor","Bottle Size","Package"],
      getKey: r => `${r.chain_name??""}\x00${r.account_name??""}\x00${r.account_address??""}\x00${r.account_city??""}\x00${r.states?.state_name??""}\x00${r.states?.distributor??""}\x00${r.flavors?.name??""}\x00${r.sizes?.name??""}\x00${r.pack_sizes?.name??""}`,
      getVals: r => [r.chain_name??"—",r.account_name??"—",r.account_address??"—",r.account_city??"—",r.states?.state_name??"—",r.states?.distributor??"—",r.flavors?.name??"—",r.sizes?.name??"—",r.pack_sizes?.name??"—"],
    };
  }
  const cfg = configs[groupBy] || configs[""];

  const groups = {};
  visibleRows.forEach(r => {
    const key = cfg.getKey(r);
    if (!groups[key]) groups[key] = { vals: cfg.getVals(r), months: {} };
    const ck = `${r.year}-${String(r.month).padStart(2, "0")}`;
    groups[key].months[ck] = (groups[key].months[ck] ?? 0) + (Number(r.case_sales) || 0);
  });

  const colKeySet = new Set();
  Object.values(groups).forEach(g => Object.keys(g.months).forEach(k => colKeySet.add(k)));
  const activeCols = [...colKeySet].sort().map(k => {
    const [y, m] = k.split("-");
    const abbr   = MONTHS[Number(m) - 1]?.slice(0, 3) ?? m;
    return { key: k, label: multiYear ? `${abbr} ${String(y).slice(2)}` : abbr };
  });

  const pivotRows = Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => g);

  return { dimLabels: cfg.dimLabels, pivotRows, activeCols };
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DepletionsPage({ onHome }) {
  const [rows,          setRows]          = useState([]);
  const [states,        setStates]        = useState([]);
  const [flavors,       setFlavors]       = useState([]);
  const [sizes,         setSizes]         = useState([]);
  const [packSizes,     setPackSizes]     = useState([]);
  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [toast,         setToast]         = useState(null);
  const [luOpen,        setLuOpen]        = useState(false);
  const [luData,        setLuData]        = useState(null);
  const [luLoading,     setLuLoading]     = useState(false);
  const [viewMode,      setViewMode]      = useState("grid");
  const [exportOpen,    setExportOpen]    = useState(false);
  const [importOpen,    setImportOpen]    = useState(false);

  const [groupBy,           setGroupBy]           = useState("");

  const [filterYear,        setFilterYear]        = useState("");
  const [filterRegion,      setFilterRegion]      = useState("");
  const [filterState,       setFilterState]       = useState("");
  const [filterDistributor, setFilterDistributor] = useState("");
  const [filterTag,         setFilterTag]         = useState("");
  const [filterFlavor,      setFilterFlavor]      = useState("");
  const [filterSize,        setFilterSize]        = useState("");
  const [filterPackSize,    setFilterPackSize]    = useState("");
  // Retail-account (outlet) filters. Only meaningful once a sheet carries
  // per-door rows; the selectors render disabled when the data is state-level so
  // the capability is discoverable rather than absent.
  const [filterChain,       setFilterChain]       = useState("");
  const [filterAccount,     setFilterAccount]     = useState("");
  const [filterCity,        setFilterCity]        = useState("");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [deps, sts, flvs, szs, pkgs, prods] = await Promise.all([
        loadDepletions({
          year:       filterYear     ? Number(filterYear) : undefined,
          flavorId:   filterFlavor   || undefined,
          sizeId:     filterSize     || undefined,
          packSizeId: filterPackSize || undefined,
        }),
        listSalesStates(),
        listFlavors(),
        listSizes(),
        listPackSizes(),
        listProducts(),
      ]);
      setRows(deps);
      setStates(sts);
      setFlavors(flvs);
      setSizes(szs);
      setPackSizes(pkgs);
      setProducts(prods);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [filterYear, filterFlavor, filterSize, filterPackSize]);

  useEffect(() => { load(); }, [load]);

  // Distinct region options from states table
  const regionOptions = useMemo(() =>
    [...new Set(states.map(s => s.region).filter(Boolean))].sort()
  , [states]);

  // Unique state names filtered by selected region (deduplicated)
  const filteredStates = useMemo(() => {
    const source = filterRegion ? states.filter(s => s.region === filterRegion) : states;
    const seen = new Set();
    return source.filter(s => {
      if (seen.has(s.state_name)) return false;
      seen.add(s.state_name);
      return true;
    });
  }, [states, filterRegion]);

  // Distinct distributor options — filtered by selected region and state
  const distributorOptions = useMemo(() => {
    const source = rows.filter(r => {
      if (filterRegion && r.states?.region     !== filterRegion) return false;
      if (filterState  && r.states?.state_name !== filterState)  return false;
      return true;
    });
    return [...new Set(source.map(r => r.states?.distributor).filter(Boolean))].sort();
  }, [rows, filterRegion, filterState]);

  // Retail-account detail is optional: a state-level sheet leaves these NULL on
  // every row. Derive the options from the data itself. The GRID columns still
  // appear only when there is detail to show, but the Chain / Store FILTERS are
  // always rendered and simply disabled — see the selector bar below for why.
  // Years come from the DATA, not the clock. This used to be
  // `Array.from({length: 8}, (_, i) => CURRENT_YEAR - i)` — the current year and
  // the seven before it — which silently excluded both ends: a future year (the
  // importer happily accepts a `Dec-27` column, and brands enter next year's plan)
  // and anything older than seven back, whose rows kept counting toward the
  // header total and the All-Years pivot while being unreachable in the filter.
  // The current year is unioned in so the control is never empty on a fresh
  // account with no depletions yet.
  const yearOptions = useMemo(() => {
    const years = new Set(rows.map((r) => r.year).filter((y) => Number.isFinite(y)));
    years.add(CURRENT_YEAR);
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  const chainOptions = useMemo(
    () => [...new Set(rows.map((r) => r.chain_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  // Cascades off chain + the geography filters, so picking a chain narrows the
  // store list instead of offering every store the tenant has.
  const accountOptions = useMemo(() => {
    const source = rows.filter((r) => {
      if (filterChain  && r.chain_name        !== filterChain)  return false;
      if (filterCity   && r.account_city      !== filterCity)   return false;
      if (filterRegion && r.states?.region     !== filterRegion) return false;
      if (filterState  && r.states?.state_name !== filterState)  return false;
      return true;
    });
    return [...new Set(source.map((r) => r.account_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [rows, filterChain, filterCity, filterRegion, filterState]);

  // Cities cascade off chain + geography for the same reason stores do, but NOT
  // off the chosen store — picking a store already implies its city, and hiding
  // every other city would make the control look broken.
  const cityOptions = useMemo(() => {
    const source = rows.filter((r) => {
      if (filterChain  && r.chain_name         !== filterChain)  return false;
      if (filterRegion && r.states?.region     !== filterRegion) return false;
      if (filterState  && r.states?.state_name !== filterState)  return false;
      return true;
    });
    return [...new Set(source.map((r) => r.account_city).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [rows, filterChain, filterRegion, filterState]);

  const hasOutletDetail =
    chainOptions.length > 0 || accountOptions.length > 0 || cityOptions.length > 0;

  // A selected store can be stranded by a later region/state change — the filter
  // would then match nothing and the grid would read as empty rather than
  // over-filtered. Drop the selection when it leaves the available set.
  useEffect(() => {
    if (filterAccount && !accountOptions.includes(filterAccount)) setFilterAccount("");
  }, [accountOptions, filterAccount]);

  useEffect(() => {
    if (filterCity && !cityOptions.includes(filterCity)) setFilterCity("");
  }, [cityOptions, filterCity]);

  // Tags come from the account's own SKUs (System Settings → Product SKUs).
  // This list used to be four hardcoded flavor names, which offered every tenant
  // tags they had never used and hid the ones they had.
  const tagOptions = useMemo(
    () => [...new Set(products.flatMap((p) => (Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : [])))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const tagMap = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const key = `${p.flavor_id}|${p.size_id}|${p.pack_size_id}`;
      map[key] = Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : [];
    });
    return map;
  }, [products]);

  // Client-side filtering for region, state, distributor, tag, flavor, size, packSize
  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterRegion      && r.states?.region       !== filterRegion)       return false;
      if (filterState       && r.states?.state_name   !== filterState)        return false;
      if (filterDistributor && r.states?.distributor  !== filterDistributor)  return false;
      if (filterTag) {
        const tags = tagMap[`${r.flavors?.id}|${r.sizes?.id}|${r.pack_sizes?.id}`] ?? [];
        if (!tags.includes(filterTag)) return false;
      }
      if (filterFlavor      && String(r.flavors?.id)    !== filterFlavor)     return false;
      if (filterSize        && String(r.sizes?.id)      !== filterSize)        return false;
      if (filterPackSize    && String(r.pack_sizes?.id) !== filterPackSize)   return false;
      if (filterChain       && r.chain_name             !== filterChain)      return false;
      if (filterAccount     && r.account_name           !== filterAccount)    return false;
      if (filterCity        && r.account_city           !== filterCity)       return false;
      return true;
    });
  }, [rows, filterRegion, filterState, filterDistributor, filterTag, filterFlavor, filterSize, filterPackSize, filterChain, filterAccount, filterCity, tagMap]);

  const totalCases = useMemo(() =>
    filtered.reduce((s, r) => s + (Number(r.case_sales) || 0), 0)
  , [filtered]);

  const hasFilters = filterYear || filterRegion || filterState || filterDistributor || filterTag || filterFlavor || filterSize || filterPackSize || filterChain || filterAccount || filterCity;

  const clearFilters = () => {
    setFilterYear(""); setFilterRegion(""); setFilterState(""); setFilterDistributor("");
    setFilterTag(""); setFilterFlavor(""); setFilterSize(""); setFilterPackSize("");
    setFilterChain(""); setFilterAccount(""); setFilterCity(""); setGroupBy("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await deleteDepletion(id);
      setRows(prev => prev.filter(r => r.id !== id));
      showToast("Row deleted.");
    } catch (err) { showToast(err.message, "error"); }
  };


  const fmtNum = n => Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const fmtLU = d => {
    if (!d) return "No data";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      + " at "
      + dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const handleLastUpdated = async () => {
    setLuOpen(true);
    setLuLoading(true);
    setLuData(null);
    try {
      const data = await loadDistributorLastUpdatedDepletions(filterDistributor);
      setLuData(data);
    } catch (e) {
      setLuData({ error: e.message });
    } finally {
      setLuLoading(false);
    }
  };

  const handleExport = async ({ includeChart, chartType, chartGroupBy } = {}) => {
    const { dimLabels, pivotRows, activeCols } = buildExportPivot(filtered, filterYear, groupBy);

    const columns = [
      ...dimLabels.map((label, i) => ({ label, key: `d${i}`, kind: "dim" })),
      ...activeCols.map(c => ({ label: c.label, key: c.key, kind: "num", width: 9.5 })),
      { label: "Total", key: "__total", kind: "num", width: 12 },
    ];
    const rows = pivotRows.map(pr => {
      const o = {};
      pr.vals.forEach((v, i) => { o[`d${i}`] = v; });
      let total = 0;
      activeCols.forEach(c => { const v = pr.months[c.key] || 0; o[c.key] = v > 0 ? Math.round(v) : null; total += v; });
      o.__total = total > 0 ? Math.round(total) : null;
      return o;
    });
    const totals = { d0: "TOTAL" };
    activeCols.forEach(c => { const s = pivotRows.reduce((sum, r) => sum + (r.months[c.key] || 0), 0); totals[c.key] = s > 0 ? Math.round(s) : null; });
    totals.__total = pivotRows.reduce((s, r) => s + activeCols.reduce((ss, c) => ss + (r.months[c.key] || 0), 0), 0) || null;

    const nameOf = (list, id) => (id ? (list.find(x => String(x.id) === String(id))?.name || "") : "");
    const filtersArr = [
      ["Year", filterYear], ["Region", filterRegion], ["State", filterState],
      ["Distributor", filterDistributor], ["TAG", filterTag],
      ["Flavor", nameOf(flavors, filterFlavor)], ["Bottle Size", nameOf(sizes, filterSize)],
      ["Package", nameOf(packSizes, filterPackSize)],
      ["Group By", groupBy ? (GROUP_BY_OPTIONS.find(o => o.value === groupBy)?.label || "") : ""],
    ].filter(([, v]) => v);

    // Optional chart worksheet (2nd tab) — a timeline grouped by the chosen dim.
    let chart = null;
    if (includeChart) {
      const gb = chartGroupBy || "flavor_size";
      const { labels, series } = depletionSeries(filtered, gb);
      if (series.length && labels.length) {
        const gbLabel = CHART_GROUP_BY_OPTIONS.find(o => o.value === gb)?.label || "";
        const svg = buildTimelineChartSvg({
          labels, series, type: chartType || "bar", axisLabel: "CASES",
          title: `Depletions${gbLabel ? ` by ${gbLabel}` : ""}${filterYear ? ` — ${filterYear}` : ""}`,
        });
        const png = await svgToPng(svg, 920, 470);
        if (png) chart = { name: "Depletions Chart", base64: png.base64, width: png.width, height: png.height };
      }
    }

    const res = await exportBranded({
      title: "Depletion Data",
      subtitle: filterYear ? `Year: ${filterYear}` : "All Years",
      filters: filtersArr,
      columns, rows, totals,
      sheetName: "Depletions",
      note: "Depletion sales in physical cases. Generated by Brand Command — Depletions & Inventory.",
      chart,
    });
    return LOGO_NOTICE[res?.logo] || null;
  };

  return (
    <div className="di-module dep-page">

      {/* ── Toolbar ── */}
      <div className="dashboard-toolbar">
        <div className="toolbar-left">
          <HomeButton onClick={onHome} />
          <span className="toolbar-title">Depletion Data</span>
          {!loading && (
            <span className="toolbar-refresh-time">
              {filtered.length.toLocaleString()} rows &middot; <span style={{ color:"var(--o-text)", fontWeight:600 }}>{fmtNum(totalCases)}</span> total cases
            </span>
          )}
        </div>
        <div className="toolbar-right">
          <button className="toolbar-btn" onClick={handleLastUpdated}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Last Updated
          </button>
          <button className="toolbar-btn" onClick={() => setImportOpen(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Import
          </button>
          <button className="toolbar-btn export-btn" onClick={() => setExportOpen(true)} disabled={filtered.length === 0}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* ── Filter bar — wrapped in MobileFilterDrawer for mobile collapse ── */}
      <MobileFilterDrawer
        summary={[
          filterYear, filterRegion, filterState, filterDistributor,
          filterTag, filterFlavor, filterSize, filterPackSize,
        ].filter(Boolean).join(" · ")}
        activeCount={
          [filterYear, filterRegion, filterState, filterDistributor, filterTag, filterFlavor, filterSize, filterPackSize].filter(Boolean).length
        }
        onReset={clearFilters}
      >
      <div className="selector-bar">
        <div className="selector-group">
          <label className="selector-label">Year</label>
          <select className="selector-select" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            <option value="">— All Years —</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Region</label>
          <select className="selector-select" value={filterRegion} onChange={e => { setFilterRegion(e.target.value); setFilterState(""); }}>
            <option value="">— All Regions —</option>
            {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">State</label>
          <select className="selector-select" value={filterState} onChange={e => setFilterState(e.target.value)}>
            <option value="">— All States —</option>
            {filteredStates.map(s => <option key={s.state_name} value={s.state_name}>{s.state_name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Distributor</label>
          <select className="selector-select" value={filterDistributor} onChange={e => setFilterDistributor(e.target.value)}>
            <option value="">— All Distributors —</option>
            {distributorOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        {/* Outlet filters are always shown, but disabled until the tenant's data
            actually carries store detail. They used to be hidden entirely; that
            made the capability invisible to exactly the people who need to decide
            whether adding the Chain / Store columns to their sheet is worth it —
            a disabled control that names the missing column teaches, an absent
            one doesn't. The store list still cascades off the chosen chain. */}
        <div className="selector-group">
          <label className="selector-label">Chain</label>
          <select
            className="selector-select"
            value={filterChain}
            disabled={!hasOutletDetail}
            title={hasOutletDetail ? undefined : NO_OUTLET_DETAIL_HINT}
            onChange={e => { setFilterChain(e.target.value); setFilterAccount(""); }}
          >
            <option value="">{hasOutletDetail ? "— All Chains —" : "— No chain data —"}</option>
            {chainOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Store</label>
          <select
            className="selector-select"
            value={filterAccount}
            disabled={!hasOutletDetail}
            title={hasOutletDetail ? undefined : NO_OUTLET_DETAIL_HINT}
            onChange={e => setFilterAccount(e.target.value)}
          >
            <option value="">{hasOutletDetail ? "— All Stores —" : "— No store data —"}</option>
            {accountOptions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">City</label>
          <select
            className="selector-select"
            value={filterCity}
            disabled={!hasOutletDetail}
            title={hasOutletDetail ? undefined : NO_OUTLET_DETAIL_HINT}
            onChange={e => { setFilterCity(e.target.value); setFilterAccount(""); }}
          >
            <option value="">{hasOutletDetail ? "— All Cities —" : "— No city data —"}</option>
            {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">TAG</label>
          <select className="selector-select" value={filterTag} onChange={e => setFilterTag(e.target.value)}>
            <option value="">— All TAGs —</option>
            {tagOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Flavor</label>
          <select className="selector-select" value={filterFlavor} onChange={e => setFilterFlavor(e.target.value)}>
            <option value="">— All Flavors —</option>
            {flavors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Bottle Size</label>
          <select className="selector-select" value={filterSize} onChange={e => setFilterSize(e.target.value)}>
            <option value="">— All Sizes —</option>
            {sizes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="selector-group">
          <label className="selector-label">Package</label>
          <select className="selector-select" value={filterPackSize} onChange={e => setFilterPackSize(e.target.value)}>
            <option value="">— All Packages —</option>
            {packSizes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="dep-groupby-wrap">
          <div className="selector-group">
            <label className="selector-label dep-groupby-label">Group By</label>
            <select className="selector-select dep-groupby-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              {GROUP_BY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              {/* Offered but disabled without store detail — grouping by Store on a
                  state-level dataset would produce one "—" row, but omitting the
                  options entirely hides that the grouping exists at all. */}
              {OUTLET_GROUP_BY_OPTIONS.map(o => (
                <option key={o.value} value={o.value} disabled={!hasOutletDetail}>
                  {hasOutletDetail ? o.label : `${o.label} (needs store data)`}
                </option>
              ))}
            </select>
          </div>
        </div>
        {hasFilters && (
          <button className="selector-clear" onClick={clearFilters}>Clear</button>
        )}
        <div className="view-toggle">
            <button
              className={`view-toggle-btn${viewMode === "grid" ? " view-toggle-btn-active" : ""}`}
              onClick={() => setViewMode("grid")} title="Grid view"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
              </svg>
              Grid
            </button>
            <button
              className={`view-toggle-btn${viewMode === "chart" ? " view-toggle-btn-active" : ""}`}
              onClick={() => setViewMode("chart")} title="Chart view"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
              </svg>
              Chart
            </button>
          </div>
      </div>
      </MobileFilterDrawer>

      {/* ── Content ── */}
      <div className="dashboard-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Loading depletion data…</div>
            <div className="loading-sub">Connecting to database</div>
          </div>
        )}
        {!loading && error && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <div className="error-title">Failed to load data</div>
            <div className="error-msg">{error}</div>
            <button className="error-retry" onClick={load}>Retry</button>
          </div>
        )}
        {!loading && !error && viewMode === "chart" && (
          <DepletionsChart rows={filtered} groupBy={groupBy || "flavor_size"} />
        )}
        {!loading && !error && viewMode === "grid" && (
          rows.length === 0 ? (
            <div className="grid-prompt">
              <div className="grid-prompt-icon">📊</div>
              <div className="grid-prompt-text">
                No depletion data yet. Connect a Google Sheet in <strong>System Settings → Sync Settings</strong> to bring depletions in.
              </div>
            </div>
          ) : (
            <DepletionsGrid rows={filtered} filterYear={filterYear} groupBy={groupBy} />
          )
        )}
      </div>

      <ExportOptionsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        groupByOptions={CHART_GROUP_BY_OPTIONS}
        defaultGroupBy={groupBy && groupBy !== "" ? groupBy : "flavor_size"}
        showChartType
        chartNote="Adds a second worksheet tab with a depletions timeline chart."
        onConfirm={handleExport}
      />

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        dataset="depletions"
        states={states}
        flavors={flavors}
        sizes={sizes}
        packSizes={packSizes}
        products={products}
        onImported={async ({ inserted, skipped, noSkuRows = 0, periods, warning }) => {
          await load();
          // backlog APP-334: the import did not complete cleanly. The modal is
          // still open with the full explanation — the toast must not sit
          // behind it claiming a clean import.
          if (warning) { showToast(warning, "error"); return; }
          // noSkuRows (backlog item 104) is not the same failure as `skipped`:
          // those rows WERE written, attached to no product. Both must colour
          // the toast, or the import still reads as clean.
          showToast(
            `Imported ${inserted.toLocaleString()} value${inserted === 1 ? "" : "s"} across ${periods} month${periods === 1 ? "" : "s"}`
            + (skipped ? ` · ${skipped} row${skipped === 1 ? "" : "s"} skipped` : "")
            + (noSkuRows ? ` · ⚠ ${noSkuRows.toLocaleString()} row${noSkuRows === 1 ? "" : "s"} with no SKU match` : ""),
            (skipped || noSkuRows) ? "error" : "success",
          );
        }}
      />

      {toast && (
        <div className={`dep-toast${toast.type === "error" ? " dep-toast-error" : ""}`}>
          {toast.msg}
        </div>
      )}

      {luOpen && (
        <div className="dep-backdrop" onClick={() => setLuOpen(false)}>
          <div className="dep-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="dep-modal-header">
              <span className="dep-modal-title">Last Updated / Modified</span>
              <button className="dep-modal-close" onClick={() => setLuOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="dep-modal-body">
              {luLoading ? (
                <div style={{ color: "var(--muted)", fontSize: 14 }}>Loading…</div>
              ) : luData?.error ? (
                <div style={{ color: "var(--error)", fontSize: 14 }}>{luData.error}</div>
              ) : !luData?.length ? (
                <div style={{ color: "var(--muted)", fontSize: 14 }}>No data found.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Distributor</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Last Updated</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--o-text)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>Last Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {luData.map(row => (
                      <tr key={row.distributor}>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--heading)", fontWeight: 500 }}>{row.distributor}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>{fmtLU(row.last_depletions_update)}</td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>{fmtLU(row.last_depletions_modified)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
